import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { TrendingUp, Calendar, Heart, Brain, Moon, Activity } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProgressPage = () => {
  const { user } = useAuth();
  const [progressData, setProgressData] = useState([]);
  const [checkins, setCheckins] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [progressRes, checkinsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/progress`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/checkins?limit=30`, { withCredentials: true })
      ]);
      setProgressData(progressRes.data);
      setCheckins(checkinsRes.data);
    } catch (error) {
      console.error('Progress data load error:', error);
    }
  };

  const getMoodEmoji = (mood) => {
    if (mood <= 3) return '😔';
    if (mood <= 5) return '😐';
    if (mood <= 7) return '🙂';
    return '😊';
  };

  return (
    <div className="min-h-screen">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-semibold text-white mb-2" data-testid="progress-title">
            İlerleme Takibi
          </h1>
          <p className="text-base leading-relaxed text-white/60">
            Gelişiminizi ve geçmiş kayıtlarınızı görüntüleyin
          </p>
        </div>

        {/* Progress Chart */}
        <div className="glass-card-md p-6 sm:p-8 mb-8" data-testid="progress-chart">
          <div className="flex items-center mb-6">
            <TrendingUp className="w-8 h-8 text-blue-500 mr-3" />
            <div>
              <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-white/90">Gelişim Grafikleri</h2>
              <p className="text-sm text-white/50 mt-1">Örnek takip görünümü (gerçek veriler yakında)</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={progressData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" stroke="#64748b" style={{ fontSize: '12px' }} />
              <YAxis stroke="#64748b" style={{ fontSize: '12px' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'transparent',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '8px'
                }}
              />
              <Legend />
              <Line type="monotone" dataKey="attention" stroke="#0ea5e9" strokeWidth={2} name="Dikkat" />
              <Line type="monotone" dataKey="memory" stroke="#8b5cf6" strokeWidth={2} name="Hafıza" />
              <Line type="monotone" dataKey="mood" stroke="#ec4899" strokeWidth={2} name="Ruh Hali" />
              <Line type="monotone" dataKey="sleep" stroke="#10b981" strokeWidth={2} name="Uyku" />
              <Line type="monotone" dataKey="functionality" stroke="#f59e0b" strokeWidth={2} name="İşlevsellik" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="glass-card-md p-6" data-testid="summary-checkins">
            <div className="flex items-center mb-3">
              <Calendar className="w-6 h-6 text-blue-500 mr-2" />
              <h3 className="text-lg font-medium text-white/90">Toplam Check-in</h3>
            </div>
            <p className="text-3xl font-semibold text-blue-600">{checkins.length}</p>
          </div>

          <div className="glass-card-md p-6" data-testid="summary-avg-mood">
            <div className="flex items-center mb-3">
              <Heart className="w-6 h-6 text-blue-500 mr-2" />
              <h3 className="text-lg font-medium text-white/90">Ortalama Ruh Hali</h3>
            </div>
            <p className="text-3xl font-semibold text-blue-600">
              {checkins.length > 0
                ? (checkins.reduce((sum, c) => sum + c.mood, 0) / checkins.length).toFixed(1)
                : '-'}/10
            </p>
          </div>

          <div className="glass-card-md p-6" data-testid="summary-avg-sleep">
            <div className="flex items-center mb-3">
              <Moon className="w-6 h-6 text-blue-500 mr-2" />
              <h3 className="text-lg font-medium text-white/90">Ortalama Uyku</h3>
            </div>
            <p className="text-3xl font-semibold text-blue-600">
              {checkins.length > 0
                ? (checkins.reduce((sum, c) => sum + c.sleep_hours, 0) / checkins.length).toFixed(1)
                : '-'} saat
            </p>
          </div>
        </div>

        {/* Recent Check-ins */}
        <div className="glass-card-md p-6 sm:p-8" data-testid="recent-checkins">
          <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-white/90 mb-6">Geçmiş Check-in Kayıtları</h2>
          {checkins.length === 0 ? (
            <p className="text-white/60">Henüz check-in kaydınız bulunmuyor.</p>
          ) : (
            <div className="space-y-3">
              {checkins.map((checkin, idx) => (
                <div key={idx} className="bg-slate-50 border border-slate-100 rounded-2xl p-4" data-testid={`checkin-item-${idx}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-white/70">{new Date(checkin.created_at).toLocaleDateString('tr-TR')}</span>
                    <span className="text-2xl">{getMoodEmoji(checkin.mood)}</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <span className="text-white/50">Ruh Hali:</span>
                      <span className="ml-2 font-medium text-white/90">{checkin.mood}/10</span>
                    </div>
                    <div>
                      <span className="text-white/50">Enerji:</span>
                      <span className="ml-2 font-medium text-white/90">{checkin.energy}/10</span>
                    </div>
                    <div>
                      <span className="text-white/50">Odak:</span>
                      <span className="ml-2 font-medium text-white/90">{checkin.focus}/10</span>
                    </div>
                    <div>
                      <span className="text-white/50">Uyku:</span>
                      <span className="ml-2 font-medium text-white/90">{checkin.sleep_hours}h</span>
                    </div>
                  </div>
                  {checkin.note && (
                    <p className="mt-2 text-sm text-white/60 italic">Not: {checkin.note}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProgressPage;